import React, { useState, useEffect } from 'react';
import {
  Select,
  Button,
  Checkbox,
  Table,
  message,
  Input,
  InputNumber,
  Popover
} from 'antd';
import axios from 'axios';
import { BASE_URL } from '../../middleware/api';
import './ImageManagementPage.css';

const { Option } = Select;

const DEFAULT_RANGE_FILTERS = {
  sightingsCount: { min: 0, max: null },
  notIdenticalSightingsCount: { min: 0, max: null },
  notIdenticalRatio: { min: 0, max: 100 }
};

const buildRangeSearchParams = (ranges) =>
  Object.entries(ranges).reduce((params, [field, range]) => {
    params[`${field}Min`] = range.min;
    if (range.max !== null && range.max !== undefined) {
      params[`${field}Max`] = range.max;
    }
    return params;
  }, {});

const getNextSortState = (sortState, field) => {
  const currentSort = sortState.find((sort) => sort.field === field);
  if (!currentSort) return [...sortState, { field, order: 'asc' }];
  if (currentSort.order === 'asc') {
    return sortState.map((sort) =>
      sort.field === field ? { ...sort, order: 'desc' } : sort
    );
  }
  return sortState.filter((sort) => sort.field !== field);
};

const RangeFilter = ({ range, defaultRange, maxLimit, onApply }) => {
  const [draft, setDraft] = useState(range);
  const [visible, setVisible] = useState(false);
  const isActive =
    range.min !== defaultRange.min || range.max !== defaultRange.max;

  useEffect(() => setDraft(range), [range]);

  const applyRange = (nextRange) => {
    onApply(nextRange);
    setVisible(false);
  };

  const content = (
    <div style={{ width: 220 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <div>
          <div style={{ marginBottom: 4 }}>Min</div>
          <InputNumber
            min={0}
            max={maxLimit}
            value={draft.min}
            onChange={(min) => setDraft((current) => ({ ...current, min }))}
            style={{ width: 96 }}
          />
        </div>
        <div>
          <div style={{ marginBottom: 4 }}>Max</div>
          <InputNumber
            min={0}
            max={maxLimit}
            value={draft.max}
            onChange={(max) => setDraft((current) => ({ ...current, max }))}
            style={{ width: 96 }}
          />
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button size='small' onClick={() => applyRange(defaultRange)}>
          Reset
        </Button>
        <Button type='primary' size='small' onClick={() => applyRange(draft)}>
          Apply
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger='click'
      placement='bottom'
      visible={visible}
      onVisibleChange={setVisible}
    >
      <Button
        size='small'
        icon='filter'
        aria-label='Filter by range'
        title='Filter by minimum and maximum'
        onClick={(event) => event.stopPropagation()}
        style={{
          flex: 'none',
          width: 28,
          height: 28,
          padding: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          color: '#1890ff'
        }}
      >
        {isActive && (
          <span
            style={{
              position: 'absolute',
              top: -3,
              right: -3,
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#f5222d',
              border: '1px solid #fff'
            }}
          />
        )}
      </Button>
    </Popover>
  );
};

const ImageManagementPage = () => {
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('pending');
  const [showConfirmedImages, setShowConfirmedImages] = useState(false);
  const [sortByLastModify, setSortByLastModify] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agencies, setAgencies] = useState([]);
  const [loadingAgencies, setLoadingAgencies] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(new Set());
  const [imageId, setImageId] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [sortState, setSortState] = useState([]);
  const [rangeFilters, setRangeFilters] = useState(DEFAULT_RANGE_FILTERS);

  // Bulk selection
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [bulkStatus, setBulkStatus] = useState(null);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const loadAgencies = async () => {
    setLoadingAgencies(true);
    const url = `${BASE_URL}/assessment_image/agency_list`;
    try {
      const response = await axios.get(url);
      if (response.data && Array.isArray(response.data)) {
        setAgencies(response.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingAgencies(false);
    }
  };

  const buildSearchParams = (overrides = {}) => {
    const params = buildRangeSearchParams(rangeFilters);
    if (selectedAgency) params.agency = selectedAgency;
    if (imageId && imageId.trim()) params.imageId = imageId.trim();
    if (selectedStatus) params.pursuable = selectedStatus === 'pending';
    params.confirmed = showConfirmedImages;
    if (sortByLastModify) {
      params.sort = 'lastModify,desc';
      params.excludeNullDates = true;
    } else if (sortState.length) {
      params.sort = sortState.map(({ field, order }) => `${field},${order}`);
    } else {
      params.sort = 'sightingsCount,desc';
    }
    return { ...params, ...overrides };
  };

  const searchOriginalImages = async (params = {}) => {
    setLoading(true);

    const defaultParams = {
      page: 0,
      size: 20,
      sort: 'sightingsCount,desc',
      ...params
    };

    Object.keys(defaultParams).forEach(
      (key) => defaultParams[key] === undefined && delete defaultParams[key]
    );

    const queryParams = new URLSearchParams();
    Object.entries(defaultParams).forEach(([key, value]) => {
      if (key === 'sort' && Array.isArray(value)) {
        value.forEach((sort) => queryParams.append('sort', sort));
      } else {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    const url = `${BASE_URL}/original_image/search?${queryString}`;

    try {
      const response = await axios.get(url);
      const data = response.data;

      if (data && data.content) {
        const transformedData = data.content.map((item, index) => ({
          key: item.id || index,
          imageId: item.imageId,
          image: item.url,
          sightingsCount: item.sightingsCount,
          agency: item.agency,
          fileName: item.fileName,
          pursuable: item.pursuable,
          status: item.pursuable ? 'pending' : 'approved',
          notIdenticalSightingsCount: item.notIdenticalSightingsCount,
          notIdenticalRatio: item.notIdenticalRatio ?? null,
          confirmed: item.confirmed || false
        }));

        setTableData(transformedData);
        setPagination((prev) => ({
          ...prev,
          total: data.totalElements,
          current: data.number + 1
        }));
      }
    } catch (error) {
      console.error('Request failed:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    searchOriginalImages(
      buildSearchParams({ pursuable: true, confirmed: false })
    );
    loadAgencies();
  }, []);

  const handleSearch = () => {
    setSortState([]);
    const params = buildSearchParams({
      page: 0,
      size: pagination.pageSize,
      sort: sortByLastModify ? 'lastModify,desc' : 'sightingsCount,desc'
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSelectedKeys([]);
    searchOriginalImages(params);
  };

  const handleAgencyChange = (value) => setSelectedAgency(value);
  const handleFilterStatusChange = (value) => setSelectedStatus(value);

  const handleSortByLastModifyChange = (checked) => {
    setSortByLastModify(checked);
    setSortState([]);
    const params = buildSearchParams({ page: 0, size: pagination.pageSize });
    if (checked) {
      params.sort = 'lastModify,desc';
      params.excludeNullDates = true;
    } else {
      delete params.sort;
      delete params.excludeNullDates;
    }
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSelectedKeys([]);
    searchOriginalImages(params);
  };

  const handleMultiSort = (field) => {
    const newSort = getNextSortState(sortState, field);

    setSortState(newSort);
    setSortByLastModify(false);
    const params = buildSearchParams({
      page: 0,
      size: pagination.pageSize,
      sort: newSort.length
        ? newSort.map(({ field: sortField, order }) => `${sortField},${order}`)
        : 'sightingsCount,desc',
      excludeNullDates: undefined
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSelectedKeys([]);
    searchOriginalImages(params);
  };

  const updatePursuableStatus = async (ids, pursuable) => {
    const response = await axios.post(
      `${BASE_URL}/original_image/update-pursuable`,
      {
        ids,
        pursuable
      }
    );
    if (response.status === 200) return response.data;
    throw new Error('Failed to update pursuable status');
  };

  const updateConfirmedStatus = async (ids, confirmed) => {
    const response = await axios.post(
      `${BASE_URL}/original_image/update-confirmed`,
      {
        ids,
        confirmed
      }
    );
    if (response.status === 200) return response.data;
    throw new Error('Failed to update confirmed status');
  };

  const handleStatusChange = async (value, record) => {
    const newPursuableValue = value === 'pending';
    setUpdatingStatus((prev) => new Set(prev).add(record.key));
    try {
      await updatePursuableStatus([record.key], newPursuableValue);
      message.success(
        `Status updated to ${
          newPursuableValue ? 'Active' : 'Not Pursuable'
        } successfully.`
      );
    } catch (error) {
      message.error('Failed to update status. Please try again.');
    } finally {
      setTimeout(() => {
        searchOriginalImages(
          buildSearchParams({
            page: pagination.current - 1,
            size: pagination.pageSize
          })
        );
      }, 500);
      setUpdatingStatus((prev) => {
        const next = new Set(prev);
        next.delete(record.key);
        return next;
      });
    }
  };

  const handleConfirmedChange = async (checked, record) => {
    setUpdatingStatus((prev) => new Set(prev).add(record.key));
    try {
      await updateConfirmedStatus([record.key], checked);
      message.success(
        `Image marked as ${checked ? 'confirmed' : 'unconfirmed'} successfully.`
      );
    } catch (error) {
      message.error('Failed to update confirmation status. Please try again.');
    } finally {
      setTimeout(() => {
        searchOriginalImages(
          buildSearchParams({
            page: pagination.current - 1,
            size: pagination.pageSize
          })
        );
      }, 500);
      setUpdatingStatus((prev) => {
        const next = new Set(prev);
        next.delete(record.key);
        return next;
      });
    }
  };

  const handleBulkUpdate = async () => {
    if (!selectedKeys.length || bulkStatus === null) return;
    setBulkUpdating(true);
    try {
      const pursuable = bulkStatus === 'pending';
      await updatePursuableStatus(selectedKeys, pursuable);
      message.success(
        `${selectedKeys.length} image(s) updated to ${
          pursuable ? 'Active' : 'Not Pursuable'
        }.`
      );
      setSelectedKeys([]);
      setBulkStatus(null);
    } catch (error) {
      message.error('Failed to update some images. Please try again.');
    } finally {
      await setTimeout(() => {
        searchOriginalImages(
          buildSearchParams({
            page: pagination.current - 1,
            size: pagination.pageSize
          })
        );
      }, 1000);
      setBulkUpdating(false);
    }
  };

  const handleClearFilters = () => {
    setSelectedAgency(null);
    setSelectedStatus('pending');
    setShowConfirmedImages(false);
    setSortByLastModify(false);
    setImageId('');
    setSortState([]);
    setRangeFilters(DEFAULT_RANGE_FILTERS);
    setSelectedKeys([]);
    setPagination((prev) => ({ ...prev, current: 1 }));
    searchOriginalImages({
      page: 0,
      size: pagination.pageSize,
      pursuable: true,
      confirmed: false,
      ...buildRangeSearchParams(DEFAULT_RANGE_FILTERS)
    });
  };

  const handleRangeFilterApply = (field, range) => {
    if (
      range.max !== null &&
      range.max !== undefined &&
      range.min > range.max
    ) {
      message.error('Minimum value cannot be greater than maximum value.');
      return;
    }
    const nextRanges = {
      ...rangeFilters,
      [field]: { ...range, min: range.min ?? 0 }
    };
    setRangeFilters(nextRanges);
    const params = buildSearchParams({
      page: 0,
      size: pagination.pageSize,
      ...buildRangeSearchParams(nextRanges)
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSelectedKeys([]);
    searchOriginalImages(params);
  };

  const handleTableChange = (newPagination) => {
    const params = buildSearchParams({
      page: newPagination.current - 1,
      size: newPagination.pageSize
    });

    setPagination(newPagination);
    setSelectedKeys([]);
    searchOriginalImages(params);
  };

  const renderSortTitle = (title, field) => {
    const sortIndex = sortState.findIndex((sort) => sort.field === field);
    const sort = sortIndex >= 0 ? sortState[sortIndex] : null;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <span
          role='button'
          tabIndex={0}
          onClick={() => handleMultiSort(field)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleMultiSort(field);
            }
          }}
          style={{ cursor: 'pointer', userSelect: 'none', flex: 1 }}
          title='Click to add or change sort priority'
        >
          {title}{' '}
          <span
            aria-label={
              sort
                ? `${sort.order === 'asc' ? 'Ascending' : 'Descending'} sort`
                : 'Sort this column'
            }
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              marginLeft: 4,
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              color: sort ? '#1890ff' : '#8c8c8c',
              fontSize: 16,
              lineHeight: 1,
              verticalAlign: 'middle'
            }}
          >
            {sort ? (sort.order === 'asc' ? '↑' : '↓') : '↕'}
          </span>
        </span>
        <RangeFilter
          range={rangeFilters[field]}
          defaultRange={DEFAULT_RANGE_FILTERS[field]}
          maxLimit={field === 'notIdenticalRatio' ? 100 : undefined}
          onApply={(range) => handleRangeFilterApply(field, range)}
        />
      </div>
    );
  };

  const columns = [
    {
      title: 'Image ID',
      dataIndex: 'imageId',
      key: 'imageId',
      width: 120
    },
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      width: 100,
      render: (imageUrl) => (
        <img
          src={imageUrl}
          alt='Preview'
          style={{
            width: '100%',
            objectFit: 'cover',
            borderRadius: '4px',
            border: '1px solid #d9d9d9'
          }}
        />
      )
    },
    {
      title: renderSortTitle('Number of sightings', 'sightingsCount'),
      dataIndex: 'sightingsCount',
      key: 'sightingsCount',
      width: 160,
      sorter: false
    },
    {
      title: renderSortTitle('Not identical', 'notIdenticalSightingsCount'),
      dataIndex: 'notIdenticalSightingsCount',
      key: 'notIdenticalSightingsCount',
      width: 130,
      sorter: false
    },
    {
      title: renderSortTitle('Not identical ratio', 'notIdenticalRatio'),
      dataIndex: 'notIdenticalRatio',
      key: 'notIdenticalRatio',
      width: 150,
      sorter: false,
      render: (val) => (val !== null && val !== undefined ? val : '—')
    },
    {
      title: 'Agency',
      dataIndex: 'agency',
      key: 'agency',
      width: 120
    },
    {
      title: 'Image/sighting status',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (status, record) => (
        <Select
          value={status}
          onChange={(value) => handleStatusChange(value, record)}
          style={{ width: '100%' }}
          size='small'
          loading={updatingStatus.has(record.key)}
          disabled={updatingStatus.has(record.key)}
        >
          <Option value='pending'>Active</Option>
          <Option value='approved'>Not Pursuable</Option>
        </Select>
      )
    },
    {
      title: 'Confirmed',
      dataIndex: 'confirmed',
      key: 'confirmed',
      width: 120,
      render: (confirmed, record) => (
        <Checkbox
          checked={confirmed}
          onChange={(e) => handleConfirmedChange(e.target.checked, record)}
          disabled={updatingStatus.has(record.key)}
        >
          {updatingStatus.has(record.key) ? 'Updating...' : 'Confirmed'}
        </Checkbox>
      )
    }
  ];

  return (
    <div className='image-management-page'>
      <div className='page-content'>
        <div className='filters-section'>
          <div className='filter-item'>
            <label className='filter-label'>Image ID</label>
            <Input
              placeholder='Enter image ID'
              value={imageId}
              onChange={(e) => setImageId(e.target.value)}
              onPressEnter={handleSearch}
              style={{ width: '200px' }}
            />
          </div>

          <div className='filter-item'>
            <label className='filter-label'>Agency</label>
            <Select
              placeholder='Select Agency'
              value={selectedAgency}
              onChange={handleAgencyChange}
              style={{ width: '200px' }}
              allowClear
              loading={loadingAgencies}
              showSearch
              filterOption={(input, option) =>
                option &&
                option.props &&
                option.props.children &&
                option.props.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              {agencies.map((agency, index) => (
                <Option key={index} value={agency}>
                  {agency}
                </Option>
              ))}
            </Select>
          </div>

          <div className='filter-item'>
            <label className='filter-label'>Status</label>
            <Select
              placeholder='Select Status'
              value={selectedStatus}
              onChange={handleFilterStatusChange}
              style={{ width: '200px' }}
              allowClear
            >
              <Option value='pending'>Active</Option>
              <Option value='approved'>NotPursuable</Option>
            </Select>
          </div>

          <div className='filter-item'>
            <label className='filter-label'>Options</label>
            <div className='checkbox-wrapper'>
              <Checkbox
                checked={showConfirmedImages}
                onChange={(e) => setShowConfirmedImages(e.target.checked)}
              >
                Show all confirmed Images
              </Checkbox>
              <Checkbox
                checked={sortByLastModify}
                onChange={(e) => handleSortByLastModifyChange(e.target.checked)}
                style={{ marginLeft: '16px' }}
              >
                Sort by last modified
              </Checkbox>
            </div>
          </div>

          <div className='filter-item'>
            <label className='filter-label' style={{ visibility: 'hidden' }}>
              Actions
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                type='primary'
                onClick={handleSearch}
                style={{ flex: 1 }}
                size='default'
                loading={loading}
              >
                Search
              </Button>
              <Button
                onClick={handleClearFilters}
                style={{ flex: 1 }}
                size='default'
              >
                Clear
              </Button>
            </div>
          </div>
        </div>

        <div className='content-section'>
          {selectedKeys.length > 0 && (
            <div className='bulk-action-bar'>
              <span className='bulk-action-bar__count'>
                {selectedKeys.length} image(s) selected
              </span>
              <Select
                placeholder='Set status'
                value={bulkStatus}
                onChange={(val) => setBulkStatus(val)}
                style={{ width: 180 }}
                size='small'
              >
                <Option value='pending'>Active</Option>
                <Option value='approved'>Not Pursuable</Option>
              </Select>
              <Button
                type='primary'
                size='small'
                loading={bulkUpdating}
                disabled={bulkStatus === null}
                onClick={handleBulkUpdate}
              >
                Apply
              </Button>
              <Button size='small' onClick={() => setSelectedKeys([])}>
                Clear selection
              </Button>
            </div>
          )}

          <Table
            rowSelection={{
              selectedRowKeys: selectedKeys,
              onChange: (keys) => setSelectedKeys(keys),
              getCheckboxProps: (record) => ({
                disabled: updatingStatus.has(record.key)
              }),
              columnWidth: 32
            }}
            columns={columns}
            dataSource={tableData}
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
              pageSizeOptions: ['20', '50', '100', '500', '1000']
            }}
            onChange={handleTableChange}
            scroll={{ x: 900 }}
            size='middle'
            bordered
          />
        </div>
      </div>
    </div>
  );
};

export default ImageManagementPage;
